// src/components/accommodation/ExcludePackagesModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Check, Package, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { usePackagesStore } from '../../stores/packagesStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (excludedPackageIds: string[]) => void;
  tableId: string;
};

export const ExcludePackagesModal = ({ isOpen, onClose, onConfirm, tableId }: Props) => {
  const { translations } = useLanguage();
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [organizerPackages, setOrganizerPackages] = useState<{id: string, name: string, pilgrimCount: number}[]>([]);
  const [loading, setLoading] = useState(true);
  const { packages } = usePackagesStore();
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    if (isOpen && userId) {
      fetchOrganizerPackages();
    }
  }, [isOpen, userId]);

  const fetchOrganizerPackages = async () => {
    setLoading(true);
    try {
      // Get table details to find the hotel
      const tableRef = await getDocs(query(collection(firestore, 'AccommodationTables'), where('__name__', '==', tableId)));
      if (tableRef.empty) {
        console.error('Table not found');
        setLoading(false);
        return;
      }
      
      const hotelName = tableRef.docs[0].data().hn;
      
      // Get all pilgrims linked to this organizer and in this hotel
      const selectorsRef = collection(firestore, 'PilgrimSelector');
      const selectorsQuery = query(
        selectorsRef, 
        where('organizerId', '==', userId),
        where('hotels', 'array-contains', hotelName)
      );
      
      const selectorsSnapshot = await getDocs(selectorsQuery);
      
      // Count pilgrims by package name
      const packageCounts: Record<string, {count: number, name: string}> = {};
      
      selectorsSnapshot.forEach(doc => {
        const data = doc.data();
        const packageName = data.packageData.packageName;
        
        if (!packageCounts[packageName]) {
          packageCounts[packageName] = { count: 0, name: packageName };
        }
        packageCounts[packageName].count++;
      });
      
      // Convert to array and sort by name
      const packagesArray = Object.entries(packageCounts).map(([id, info]) => ({
        id,
        name: info.name,
        pilgrimCount: info.count
      })).sort((a, b) => a.name.localeCompare(b.name));
      
      setOrganizerPackages(packagesArray);
      
      // By default, select all packages (exclude all from removal)
      setSelectedPackages(packagesArray.map(p => p.id));
    } catch (error) {
      console.error('Error fetching organizer packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePackage = (packageId: string) => {
    setSelectedPackages(prev => 
      prev.includes(packageId) 
        ? prev.filter(id => id !== packageId)
        : [...prev, packageId]
    );
  };

  const handleSelectAll = () => {
    setSelectedPackages(organizerPackages.map(p => p.id));
  };

  const handleDeselectAll = () => {
    setSelectedPackages([]);
  };

  const filteredPackages = organizerPackages
    .filter(pkg => pkg.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={translations?.accommodationTable?.excludePackages || 'Exclude Package from Removal'}
    >
      <div className="space-y-4">
        <div className="bg-yellow-50 p-4 rounded-lg flex items-start gap-3">
          <AlertTriangle className="text-yellow-600 shrink-0 mt-0.5" size={20} />
          <p className="text-yellow-700 text-sm">
            {translations?.accommodationTable?.excludePackagesInfo || 
            'Select packages whose pilgrims should be excluded from the removal process. Pilgrims in selected packages will be preserved.'}
          </p>
        </div>

        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={translations?.accommodationTable?.searchPackages || 'Search packages...'}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            {selectedPackages.length} {translations?.accommodationTable?.selectedPackages || 'packages selected'}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-xs text-primary-600 hover:text-primary-700"
            >
              {translations?.accommodationTable?.selectAll || 'Select All'}
            </button>
            <button
              onClick={handleDeselectAll}
              className="px-3 py-1 text-xs text-gray-600 hover:text-gray-700"
            >
              {translations?.accommodationTable?.deselectAll || 'Deselect All'}
            </button>
          </div>
        </div>

        <div className="max-h-60 overflow-y-auto border rounded-lg">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              {translations?.accommodationTable?.loadingPackages || 'Loading packages...'}
            </div>
          ) : filteredPackages.length > 0 ? (
            <div className="divide-y">
              {filteredPackages.map(pkg => (
                <motion.div
                  key={pkg.id}
                  whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                  className={`p-3 flex items-center justify-between cursor-pointer ${
                    selectedPackages.includes(pkg.id) ? 'bg-primary-50' : ''
                  }`}
                  onClick={() => handleTogglePackage(pkg.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary-50">
                      <Package className="text-primary-500" size={16} />
                    </div>
                    <div>
                      <div className="font-medium">{pkg.name}</div>
                      <div className="text-xs text-gray-500">{pkg.pilgrimCount} pilgrims</div>
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                    selectedPackages.includes(pkg.id) 
                      ? 'border-primary-500 bg-primary-500' 
                      : 'border-gray-300'
                  }`}>
                    {selectedPackages.includes(pkg.id) && (
                      <Check className="text-white" size={12} />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              {searchTerm 
                ? (translations?.accommodationTable?.noPackagesFound || 'No packages match your search')
                : (translations?.accommodationTable?.noPackages || 'No packages available')}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.common?.cancel || 'Cancel'}
          </button>
          <button
            onClick={() => onConfirm(selectedPackages)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            {translations?.accommodationTable?.removeUnselected || 'Remove Unselected Packages'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
